import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PayPalCancelTour = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState<any>(null);

  const bookingNumber = searchParams.get('booking_number');

  useEffect(() => {
    if (bookingNumber) {
      handleCancelledPayment();
    } else {
      setLoading(false);
    }
  }, [bookingNumber]);

  const handleCancelledPayment = async () => {
    try {
      console.log('Handling cancelled PayPal payment for tour:', bookingNumber);

      // Update booking status to cancelled
      const { error: updateError } = await supabase
        .from('tour_bookings')
        .update({ 
          booking_status: 'cancelled',
          payment_status: 'cancelled'
        })
        .eq('booking_number', bookingNumber);

      if (updateError) {
        console.error('Error updating booking status:', updateError);
        toast({
          title: "Warning",
          description: "Could not update booking status. Please contact support.",
          variant: "destructive",
        });
      }

      // Get booking data for display
      const { data: booking, error: bookingError } = await supabase
        .from('tour_bookings')
        .select('*')
        .eq('booking_number', bookingNumber)
        .single();

      if (bookingError) {
        console.error('Error fetching booking:', bookingError);
      } else {
        setBookingData(booking);
      }

      toast({
        title: "Payment Cancelled",
        description: "Your PayPal payment was cancelled. You can try again or choose a different payment method.",
        variant: "destructive",
      });

    } catch (error) {
      console.error('Error handling cancelled payment:', error);
      toast({
        title: "Error",
        description: "There was an error processing the cancellation.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                <h2 className="text-2xl font-bold mb-2">Processing Cancellation</h2>
                <p className="text-muted-foreground">
                  Please wait while we process your cancelled payment...
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full w-fit">
                <XCircle className="h-12 w-12 text-orange-600 dark:text-orange-400" />
              </div>
              <CardTitle className="text-2xl text-orange-600 dark:text-orange-400">Payment Cancelled</CardTitle>
              <p className="text-muted-foreground">
                Your PayPal payment was cancelled
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {bookingData && (
                <>
                  <div className="text-center">
                    <Badge variant="secondary" className="text-lg px-4 py-2">
                      Booking #{bookingData.booking_number}
                    </Badge>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h3 className="font-semibold">Booking Details</h3>
                    <div className="grid gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer:</span>
                        <span className="font-medium">{bookingData.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tour:</span>
                        <span className="font-medium">{bookingData.tour_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">{bookingData.booking_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Time:</span>
                        <span className="font-medium">{bookingData.tour_schedule}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Amount:</span>
                        <span className="font-medium">${bookingData.total_price}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <h4 className="font-medium text-orange-800 dark:text-orange-200">Why was my payment cancelled?</h4>
                </div>
                <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                  PayPal payments can be cancelled for various reasons such as insufficient funds, 
                  payment restrictions, or if you chose to cancel during the PayPal checkout process.
                </p>
                <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">What happens next?</h4>
                <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                  <li>• Your booking has been cancelled</li>
                  <li>• No charges have been made to your account</li>
                  <li>• You can try booking again with PayPal or choose a different payment method</li>
                  <li>• Contact our support team if you need assistance</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <Button onClick={() => navigate('/contact')} variant="outline" className="flex-1">
                  Contact Support
                </Button>
                <Button onClick={() => navigate('/tours')} className="flex-1">
                  Book New Tour
                </Button>
              </div>
              
              <div className="text-center">
                <Button onClick={() => navigate('/')} variant="ghost">
                  Return to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PayPalCancelTour;